import { Heap } from 'heap-js';

import { DesignNode } from '../../../../../elements/design/design-node/design-node';
import { LineSegmentNode } from '../../../../../elements/design/design-node/utils/line-segment-node';
import { PointId } from '../../../../../elements/design/design-node/utils/point-node';

type SearchNode = {
  segment: LineSegmentNode;
  fromPoint: PointId;
  previous?: SearchNode;
  totalCost: number;
};

/**
 * Uses Dijkstra's algorithm to find the shortest euclidean distance path from point A to point B.
 */
export function findShortestPathBetweenPointIdsDijkstra(
  designNode: DesignNode,
  start: PointId,
  end: PointId,
  excludedPointIds: PointId[] = [],
): LineSegmentNode[] | undefined {
  if (start === end) {
    return [];
  }

  const visited = new Set<PointId>(excludedPointIds);
  const queue = new Heap<SearchNode>((a, b) => a.totalCost - b.totalCost);
  const bestCost = new Map<PointId, number>();

  const startSegments = designNode
    .getPointById(start)
    .segments.map((segId) => designNode.getLineSegmentById(segId));

  for (const seg of startSegments) {
    const nextPoint = seg.values[0] === start ? seg.values[1] : seg.values[0];
    if (visited.has(nextPoint)) {
      continue;
    }

    queue.push({
      segment: seg,
      fromPoint: start,
      previous: undefined,
      totalCost: seg.length,
    });
    bestCost.set(nextPoint, seg.length);
  }

  while (!queue.isEmpty()) {
    const current = queue.pop();

    if (!current) {
      break;
    }

    const currentPoint =
      current.segment.values[0] === current.fromPoint
        ? current.segment.values[1]
        : current.segment.values[0];

    if (visited.has(currentPoint)) {
      continue;
    }
    visited.add(currentPoint);

    if (currentPoint === end) {
      // reconstruct path
      const path: LineSegmentNode[] = [];
      let cursor: SearchNode | undefined = current;
      while (cursor) {
        path.unshift(cursor.segment);
        cursor = cursor.previous;
      }
      return path;
    }

    const neighborSegments = designNode
      .getPointById(currentPoint)
      .segments.map((segId) => designNode.getLineSegmentById(segId));

    for (const seg of neighborSegments) {
      if (seg.id === current.segment.id) {
        continue;
      }

      const nextPoint = seg.values[0] === currentPoint ? seg.values[1] : seg.values[0];
      if (visited.has(nextPoint)) {
        continue;
      }

      const newCost = current.totalCost + seg.length;
      const existingCost = bestCost.get(nextPoint);
      if (existingCost === undefined || newCost < existingCost) {
        bestCost.set(nextPoint, newCost);
        queue.push({
          segment: seg,
          fromPoint: currentPoint,
          previous: current,
          totalCost: newCost,
        });
      }
    }
  }

  return undefined;
}
